import { Alert, Box, Grid, Typography } from "@mui/material";
import downloadjs from "downloadjs";
import GoogleMapReact from "google-map-react";
import React, { useEffect, useState } from "react";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { AppUtils } from "source/framework/appUtils";
import { EventEditItem, IEventEditItem } from "source/models/event/eventEditItem";
import { EventTypeNode } from "source/models/eventType/eventTypeNode";
import { ISessionState } from "source/models/session/sessionState";
import { EventEditUrgencyClassField } from "ui/Scripts/source/components/event/EventEditUrgencyClassField/EventEditUrgencyClassField";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { IEventSettingSimple } from "ui/Scripts/source/models/eventSettings/eventSettingSimple";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import * as attachmentService from "../../../models/services/attachmentService";
import * as eventService from "../../../models/services/eventService";
import { AttachmentList } from "../../attachments/AttachmentList";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField/FormField";
import { FormFieldLockedToUser } from "../../framework/FormFieldLockedToUser";
import { GridRow } from "../../framework/Grid/GridRow";
import { ListDetailsPanel } from "../../framework/List/ListDetailsPanel";
import { ListDetailsPanelAccordion } from "../../framework/List/ListDetailsPanel/ListDetailsPanelAccordion";
import { AvatarName } from "../../framework/User/AvatarName";
import { ModifyUserInfo } from "../../framework/User/ModifyUserInfo";
import { MapMarker } from "ui/Scripts/source/components/framework/mapMarker";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { recordLockManager } from "../../../models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { EventEditDialogMessageList } from "../EventEditDialog/EventEditDialogMessageList";

interface EventListDetailsPanelProps {
    sessionState: ISessionState;
    companyId: string;
    eventId: string;
    dataId: string;
    editDialogVisible: boolean;
    onRefreshList: () => void;
    onClose: () => void;
}

interface EventListDetailsPanelState {
    canEditIdentificationData: boolean;
    identificationDataIsVisible: boolean;
    event: IEventEditItem;
    eventSetting: IEventSettingSimple;
    maxEventMessageAttachmentSize: number;
    lastApproveUser: IModifyUser;
    urgencyClasses: IIdColorTitle[];
    eventTypeNodes: IIdTitleNode[];
    eventTypeBNodes: IIdTitleNode[];
    eventTypeCNodes: IIdTitleNode[];
    createCompany: ICompanyMenuItem;
    companies: ICompanyMenuItem[];
    processingTimeStr: string;
    handlers: IModifyUser[];
}

export const EventListDetailsPanel = (props: EventListDetailsPanelProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState<EventListDetailsPanelState>({
        canEditIdentificationData: false,
        identificationDataIsVisible: false,
        event: null,
        eventSetting: null,
        maxEventMessageAttachmentSize: 0,
        lastApproveUser: null,
        urgencyClasses: [],
        eventTypeNodes: [],
        eventTypeBNodes: [],
        eventTypeCNodes: [],
        createCompany: null,
        companies: [],
        processingTimeStr: "",
        handlers: []
    });
    const receiverCompany = state.companies.find(i => i.id === state.event?.companyId);
    const activeCompanyIsReceivingCompany = props.companyId === state.event?.companyId;
    
    const changeState = (obj: any) => {
        setState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const loadEvent = async () => {
        if (!props.eventId) return;
        setIsLoading(true);
        const result = await AppUtils.callService(() => eventService.getEventEdit(props.companyId, props.eventId, false), false, true, () => setIsLoading(false));
        if (!result) return;
        const event = result.item;
        const firstAndLastApproved = EventEditItem.getFirstAndLastApproveHandler(event.handlers);
        const userIsCreator = event.createUser.code === props.sessionState.code;
        changeState({
            canEditIdentificationData: result.canEditIdentificationData,
            identificationDataIsVisible: userIsCreator || result.canEditIdentificationData,
            event,
            eventSetting: result.eventSetting,
            maxEventMessageAttachmentSize: result.maxEventMessageAttachmentSize,
            urgencyClasses: result.urgencyClasses,
            eventTypeNodes: EventTypeNode.getAsIdTitleNodes(result.eventTypeNodes),
            eventTypeBNodes: EventTypeNode.getAsIdTitleNodes(result.eventTypeBNodes),
            eventTypeCNodes: EventTypeNode.getAsIdTitleNodes(result.eventTypeCNodes),
            lastApproveUser: firstAndLastApproved.lastApprove,
            processingTimeStr: EventEditItem.getProcessingTimeStr(firstAndLastApproved.lastApprove, event.publishUser, event.createUser),
            isLockedToOtherUser: event.isLockedToOtherUser(props.sessionState.code),
            createCompany: result.createCompany,
            companies: result.companies,
            handlers: event.handlers,
        });
    };

    const recordUnLocked = (id: string) => {
        if (!id || !state.event?.id || state.event.id !== id) return;
        const event = new EventEditItem(state.event);
        event.lockedUser = null;
        changeState({
            event,
            isLockedToOtherUser: event.isLockedToOtherUser(props.sessionState.code),
        });
    };

    const recordLockedToMe = (id: string) => {
        if (!id || !state.event?.id || state.event.id !== id) return;
        const event = new EventEditItem(state.event);
        event.lockedUser = StoreUtils.getNowModifyUser();
        changeState({
            event,
            isLockedToOtherUser: event.isLockedToOtherUser(props.sessionState.code),
        });
    };

    useEffect(() => {
        recordLockManager.addUnlockCallBack(recordUnLocked);
        recordLockManager.addLockCallBack(recordLockedToMe);

        return () => {
            recordLockManager.removeUnlockCallBack(recordUnLocked);
            recordLockManager.removeLockCallBack(recordLockedToMe);
        };
    }, []);

    useEffect(() => {
        if (props.editDialogVisible) return;
        loadEvent();
    }, [props.companyId, props.eventId, props.dataId, props.editDialogVisible]);

    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = state.event?.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const getEventTypesString = (eventTypeNodes: IIdTitleNode[], eventTypeIds: string[]) => {
        if (!eventTypeNodes || !eventTypeIds || !eventTypeNodes.length || !eventTypeIds.length) return "";
        const result: IIdTitleNode[] = eventTypeNodes.map(
            (i) => new IdTitleNode(i)
        );
        IdTitleNode.setHierarchialTitles(result);
        IdTitleNode.sortUIdTitleNodes(result);
        return IdTitleNode.getByIds(result, eventTypeIds).map(eventType => eventType.title).join(", ");
    };

    const getDateStr = (): string => {
        if (!state.event?.date) return "";
        return Base.timeToDateStr(state.event.date) + (!Base.isZeroTime(state.event.date) ? " " + Base.timeToTimeStr(state.event.date) : "");
    };

    const handleApprove = async (userCode: string) => {
        if (!userCode) return;
        const handler = (state.handlers).find(i => i.code === userCode);
        if (!handler) return;
        if (!state.event?.immediateAction) {
            if (await AppUtils.showConfirmationDialog(Translations.ApproveEventHandlerConfirmation, false) !== ConfirmationDialogResult.Yes) {
                return;
            }
        }
        // Call server
        const result = await AppUtils.callService(() => eventService.approveEvent(props.companyId, state.event.createCompanyId, state.event.id));
        if (!result) return;
        handler.date = Base.getNowTicks();
        const handlers = ModifyUser.sortModifyUsers(state.handlers.filter(i => i.code !== userCode).concat([new ModifyUser(handler)]), { currentUserCode: props.sessionState.code });
        const firstAndLastApproved = EventEditItem.getFirstAndLastApproveHandler(handlers);
        changeState({
            handlers,
            lastApproveUser: firstAndLastApproved.lastApprove,
            processingTimeStr: EventEditItem.getProcessingTimeStr(firstAndLastApproved.lastApprove, state.event.publishUser, state.event.createUser)
        });
        props.onRefreshList();
    };

    return (
        <ListDetailsPanel
            isLoading={isLoading}
            onClose={props.onClose}
        >
            {!props.eventId &&
                <Alert severity="info">{Translations.SelectEventInListToSeeThePreview}</Alert>
            }
            {(!isLoading && props.eventId && state.event) &&
                <Box sx={{ maxHeight: "calc(100vh - 130px)", overflowY: "auto" }}>
                    <Box sx={{ padding: ".5rem 1rem 1rem" }}>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.TaskCreated} margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.event?.createUser}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.Modified} margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.event?.modifyUser}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            {state.event?.publishUser &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Published}>
                                        <ModifyUserInfo
                                            modifyUser={state.event?.publishUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            {state.lastApproveUser &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Closed}>
                                        <ModifyUserInfo
                                            modifyUser={state.lastApproveUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            {(!state.event?.publishUser || !state.lastApproveUser) && state.event?.lockedUser &&
                                <Grid item sm={6}>
                                    <FormFieldLockedToUser
                                        lockedUser={state.event?.lockedUser}
                                    />
                                </Grid>
                            }
                        </GridRow>
                        {(state.event?.publishUser && state.lastApproveUser && state.event?.lockedUser) &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormFieldLockedToUser
                                        lockedUser={state.event?.lockedUser}
                                    />
                                </Grid>
                            </GridRow>
                        }
                    </Box>
                    <ListDetailsPanelAccordion
                        title={Translations.Event}
                    >
                        {state.event?.createCompanyId !== state?.event.companyId &&
                            <GridRow>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.Sender} margin={0}>
                                        <AvatarName
                                            name={state.createCompany?.name}
                                            picture={state.createCompany?.logo}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.Receiver} margin={0}>
                                        <div className="input-group noFrame">
                                            <div className="form-control">
                                                <AvatarName
                                                    name={receiverCompany?.name}
                                                    picture={receiverCompany?.logo}
                                                />
                                            </div>
                                        </div>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        <GridRow>
                            <Grid item sm={(!activeCompanyIsReceivingCompany || (activeCompanyIsReceivingCompany && !state.event?.publishUser)) ? 3 : 4}>
                                <FormField title={Translations.Date} margin={0}>
                                    {getDateStr()}
                                </FormField>
                            </Grid>
                            <Grid item sm={(!activeCompanyIsReceivingCompany || (activeCompanyIsReceivingCompany && !state.event?.publishUser)) ? 3 : 4}>
                                <FormField title={Translations.Code} margin={0}>
                                    {state.event?.code}
                                </FormField>
                            </Grid>
                            <Grid item sm={(!activeCompanyIsReceivingCompany || (activeCompanyIsReceivingCompany && !state.event?.publishUser)) ? 3 : 4}>
                                <EventEditUrgencyClassField
                                    disabled
                                    urgencyClasses={state.urgencyClasses}
                                    urgencyClassId={state.event?.urgencyClassId}
                                    onChange={() => {}}
                                />
                            </Grid>
                            {(!activeCompanyIsReceivingCompany || (activeCompanyIsReceivingCompany && !state.event?.publishUser)) &&
                                <Grid item sm={3}>
                                    <FormField title={Translations.Unit} margin={0}>
                                        <Typography>{state.event?.createCompanyUnitCode}</Typography>
                                    </FormField>
                                </Grid>
                            }
                        </GridRow>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.Name}>
                                    {state.event?.name}
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.Observer}>
                                    {state.event?.observer}
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} keepLineBreaks>
                                    {state.event?.description}
                                </FormField>
                            </Grid>
                        </GridRow>
                        {state.eventSetting?.observerActionIsInUse() &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.ObserverAction} keepLineBreaks>
                                        {state.event?.observerAction}
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                    </ListDetailsPanelAccordion>
                    {state.event?.attachments.length > 0 &&
                        <ListDetailsPanelAccordion
                            title={Translations.Attachments}
                        >
                            <AttachmentList
                                attachments={state.event?.attachments.filter(i => i.category === 0)}
                                imageSize={state.eventSetting?.attachmentImageSize}
                                margin={0}
                                title={null}
                                isReadOnly
                                onRemoveAttachment={() => { }}
                                onDownloadAttachment={handleAttachmentDownload}
                                ignoreAspectRatio
                                onAddAttachments={() => { }}
                            />
                        </ListDetailsPanelAccordion>
                    }
                    {(state.event?.immediateAction || state.event?.preventiveAction || state.event?.rootCause) && (
                        <ListDetailsPanelAccordion
                            title={Translations.ActionsAndRootCause}
                        >
                            {state.event?.immediateAction &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField keepLineBreaks title={Translations.ImmediateAction} margin={0}>
                                            {state.event?.immediateAction}
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                            {state.event?.preventiveAction &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField keepLineBreaks title={Translations.PreventiveAction}>
                                            {state.event?.preventiveAction}
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                            {state.event?.rootCause &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField keepLineBreaks title={Translations.RootCause}>
                                            {state.event?.rootCause}
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                        </ListDetailsPanelAccordion>
                    )}
                    {state.event?.messages.length > 0 &&
                        <ListDetailsPanelAccordion
                            title={Translations.Comments}
                        >
                            <EventEditDialogMessageList
                                classes="form-group"
                                maxEventMessageAttachmentSize={state.maxEventMessageAttachmentSize}
                                companyId={props.companyId}
                                event={state.event}
                                handlers={state.handlers}
                                messages={state.event?.messages}
                                isReadOnly
                                hideNewMessageInput
                                lateThreshold={state.lastApproveUser?.date}
                                onEventMessageSaved={() => {}}
                                onEventMessageRead={() => {}}
                            />
                        </ListDetailsPanelAccordion>
                    }
                    <ListDetailsPanelAccordion
                        title={Translations.HandlersAndAttachments}
                    >
                        <GridRow>
                            {(activeCompanyIsReceivingCompany && !!state.event?.publishUser) &&
                                <Grid item sm={12}>
                                    <FormField title={Translations.Unit} margin={0}>
                                        <Typography>
                                            {state.event?.unitCode}
                                        </Typography>
                                    </FormField>
                                </Grid>
                            }
                            <Grid item sm={12}>
                                <UserBlocks
                                    companyId={props.companyId}
                                    userCode={props.sessionState.code}
                                    isReadOnly
                                    title={Translations.Handlers}
                                    noUsersMessage={Translations.AddHandlers}
                                    parentIsNew={false}
                                    parentIsNewMessage={""}
                                    users={state.handlers}
                                    margin={0}
                                    removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedHandlers}
                                    onAddUsers={() => new Promise(() => {})}
                                    onRemoveUsers={() => new Promise(() => {})}
                                    onGetUserSelectListItems={() => new Promise(() => [])}
                                    onApprove={handleApprove}
                                />
                            </Grid>
                        </GridRow>
                        {(state.processingTimeStr?.length > 0) &&
                            <GridRow>
                                <Grid item xs>
                                    <FormField title={Translations.ProcessingTime}>
                                        <Typography>{state.processingTimeStr}</Typography>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.event?.attachments.filter(i => i.category > 0.5)?.length > 0 &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Attachments}>
                                        <AttachmentList
                                            attachments={state.event?.attachments.filter(i => i.category > 0.5)}
                                            imageSize={state.eventSetting?.attachmentImageSize}
                                            margin={0}
                                            title={null}
                                            isReadOnly
                                            ignoreAspectRatio
                                            onRemoveAttachment={() => { }}
                                            onDownloadAttachment={handleAttachmentDownload}
                                            onAddAttachments={() => { }}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                    </ListDetailsPanelAccordion>
                    {state.identificationDataIsVisible &&
                        <>
                            {state.event?.eventTypeIds.length > 0 &&
                                <ListDetailsPanelAccordion
                                    title={Translations.EventTypes}
                                >
                                    <Typography>
                                        {getEventTypesString(state.eventTypeNodes, state.event?.eventTypeIds)}
                                    </Typography>
                                </ListDetailsPanelAccordion>
                            }
                            {state.event?.eventTypeBIds.length > 0 &&
                                <ListDetailsPanelAccordion
                                    title={Translations.EventTypeBs}
                                >
                                    <Typography>
                                        {getEventTypesString(state.eventTypeBNodes, state.event?.eventTypeBIds)}
                                    </Typography>
                                </ListDetailsPanelAccordion>
                            }
                            {state.event?.eventTypeCIds.length > 0 &&
                                <ListDetailsPanelAccordion
                                    title={Translations.EventTypeCs}
                                >
                                    <Typography>
                                        {getEventTypesString(state.eventTypeCNodes, state.event?.eventTypeCIds)}
                                    </Typography>
                                </ListDetailsPanelAccordion>
                            }
                            {state.canEditIdentificationData &&
                                <ListDetailsPanelAccordion
                                    title={Translations.AdditionalInformation}
                                >
                                    <GridRow>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.DirectCosts} margin={0}>
                                                <Typography>{state.event?.directCosts.toLocaleFixed(2)}</Typography>
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.IndirectCosts} margin={0}>
                                                <Typography>{state.event?.indirectCosts.toLocaleFixed(2)}</Typography>
                                            </FormField>
                                        </Grid>
                                        <Grid item sm={4}>
                                            <FormField title={Translations.Amount} margin={0}>
                                                <Typography>{state.event?.amount.toLocaleFixed(2)}</Typography>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.IdentificationDetails}>
                                                <Typography>{state.event?.identificationDetails}</Typography>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                </ListDetailsPanelAccordion>
                            }
                        </>
                    }
                    {state.eventSetting?.locationIsInUse() && state.event?.locationName &&
                        <ListDetailsPanelAccordion
                            title={Translations.Location}
                        >
                            <Box sx={{ height: "460px", width: "100%" }}>
                                <GoogleMapReact
                                    bootstrapURLKeys={{
                                        key: appConfig.googleApiKey,
                                        language: appConfig.culture
                                    }}
                                    center={{
                                        lat: state.event?.latitude,
                                        lng: state.event?.longitude,
                                    }}
                                    zoom={12}
                                    options={{
                                        streetViewControl: true,
                                        mapTypeControl: true,
                                        mapTypeControlOptions: {
                                            mapTypeIds: ["roadmap", "satellite"]
                                        }
                                    }}
                                >
                                    <MapMarker
                                        classes="red"
                                        tooltip={state.event?.locationName}
                                        {...{ lat: state.event?.latitude, lng: state.event?.longitude }}
                                    />
                                </GoogleMapReact>
                            </Box>
                        </ListDetailsPanelAccordion>
                    }
                </Box>
            }
        </ListDetailsPanel>
    );
};
